"use client";

import { trackConversion, useAnalytics } from "@analytics";
import {
	type GeneratedReportData as ReportData,
	useReportGenerationFlow,
} from "@marketing/ai-credit-report/hooks/useReportGenerationFlow";
import {
	type LocationData,
	generateAiCreditReport,
} from "@marketing/ai-credit-report/lib/api";
import {
	AI_REPORT_EVENTS,
	GTM_CONVERSION_EVENTS,
	MARKETING_EVENTS,
} from "@repo/analytics";
import { stripWwwFromDomain } from "@repo/core/client";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { AlertCircleIcon, CheckCircleIcon, XCircleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { ParseResultType, fromUrl, parseDomain } from "parse-domain";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LocationInput } from "./LocationInput";
import { ReportGeneratingPanel } from "./ReportGeneratingPanel";
import { StatusBadge } from "./StatusBadge";

function validateAndNormalizeDomain(
	input: string,
	t: any,
): { isValid: boolean; domain?: string; error?: string } {
	if (!input?.trim()) {
		return {
			isValid: false,
			error: t(
				"marketing.aiCreditReport.generateForm.validation.enterDomain",
			),
		};
	}

	// Use parse-domain's fromUrl helper to properly normalize URLs
	const parseResult = parseDomain(fromUrl(input.trim()));

	if (parseResult.type === ParseResultType.Listed) {
		// Successfully parsed and is a valid, publicly listed domain
		let domain =
			`${parseResult.subDomains.join(".")}.${parseResult.domain}.${parseResult.topLevelDomains.join(".")}`.replace(
				/^\./,
				"",
			);
		// Strip www. subdomain - we treat www.example.com as equivalent to example.com
		domain = stripWwwFromDomain(domain);
		return { isValid: true, domain };
	}

	// Handle different failure cases with specific error messages
	if (parseResult.type === ParseResultType.Reserved) {
		return {
			isValid: false,
			error: t(
				"marketing.aiCreditReport.generateForm.validation.reservedDomain",
			),
		};
	}

	if (parseResult.type === ParseResultType.NotListed) {
		return {
			isValid: false,
			error: t(
				"marketing.aiCreditReport.generateForm.validation.notListedDomain",
			),
		};
	}

	return {
		isValid: false,
		error: t(
			"marketing.aiCreditReport.generateForm.validation.invalidDomain",
		),
	};
}

export function GenerateForm() {
	const t = useTranslations();
	// The free-tool domain pages link here with the domain already decided
	// (`/ai-visibility-report?domain=example.com`), so the visitor should not
	// have to retype it. This component renders inside a Suspense boundary on
	// its page, which is what makes reading search params here safe.
	const searchParams = useSearchParams();
	const [domain, setDomain] = useState(searchParams.get("domain") ?? "");
	const [location, setLocation] = useState<LocationData | undefined>();
	const [validationError, setValidationError] = useState<string | null>(null);
	const { setPublicToken, report, isActivelyGenerating, isReportGenerating } =
		useReportGenerationFlow();
	const { trackEvent } = useAnalytics();

	// Track page view when component mounts
	useEffect(() => {
		trackEvent(MARKETING_EVENTS.CTA_CLICKED, {
			location: "ai_visibility_report_page",
			isLoggedIn: false,
		});
	}, [trackEvent]);

	const mutation = useMutation({
		mutationFn: async ({
			domain: d,
			location: loc,
		}: { domain: string; location?: LocationData }) => {
			// Get PostHog session ID if available
			let sessionId: string | undefined;
			if (typeof window !== "undefined" && (window as any).posthog) {
				sessionId = (window as any).posthog.get_session_id();
			}

			const { report } = await generateAiCreditReport(d, sessionId, loc);
			return report as ReportData;
		},
		onSuccess: (report) => {
			setValidationError(null);
			setPublicToken(report.publicToken);

			// Track successful report generation request
			trackEvent(AI_REPORT_EVENTS.REPORT_GENERATION_REQUESTED, {
				domain: report.domain,
				reportId: report.id,
				propertyId: report.id, // Using report ID as property ID for marketing reports
				source: "marketing_page",
			});
			trackConversion(GTM_CONVERSION_EVENTS.GTM_REPORT_PREVIEW_RUN, {
				reportId: report.id,
				subjectType: "brand",
			});

			toast.success(
				t("marketing.aiCreditReport.generateForm.status.startedToast"),
				{
					description: t(
						"marketing.aiCreditReport.generateForm.status.startedDescription",
					),
					icon: <CheckCircleIcon className="w-4 h-4" />,
				},
			);
		},
		onError: (error: any) => {
			// Handle API errors, including domain validation failures
			const errorMessage =
				error?.message ||
				t(
					"marketing.aiCreditReport.generateForm.status.errorDescription",
				);

			setValidationError(errorMessage);

			// Track failed report generation request
			const errorType =
				errorMessage.toLowerCase().includes("domain") ||
				errorMessage.toLowerCase().includes("invalid")
					? "domain_validation"
					: "other";

			trackEvent(AI_REPORT_EVENTS.REPORT_GENERATION_FAILED, {
				domain: domain,
				reportId: "", // No report ID available on failure
				propertyId: "",
				errorType,
				errorMessage,
			});

			// Only show toast for unexpected server errors, not domain validation errors
			if (
				!errorMessage.toLowerCase().includes("domain") &&
				!errorMessage.toLowerCase().includes("invalid")
			) {
				toast.error(
					t(
						"marketing.aiCreditReport.generateForm.status.errorToast",
					),
					{
						description: errorMessage,
						icon: <XCircleIcon className="w-4 h-4" />,
					},
				);
			}
		},
	});

	// Handle failed reports - show error message instead of redirecting
	// (the redirect-on-complete lives in useReportGenerationFlow)
	useEffect(() => {
		if (!report) return;

		if (report.status === "failed" && report.errorMessage) {
			// Track the failure (only once)
			if (!validationError) {
				trackEvent(AI_REPORT_EVENTS.REPORT_GENERATION_FAILED, {
					domain: report.domain,
					reportId: report.id,
					propertyId: report.id,
					errorType: "other",
					errorMessage: report.errorMessage,
				});
			}
		}
	}, [report, validationError, trackEvent]);

	const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setDomain(value);

		// Clear validation error when user types
		if (validationError) {
			setValidationError(null);
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validate and normalize domain using parse-domain
		const validation = validateAndNormalizeDomain(domain, t);

		if (!validation.isValid) {
			const errorMessage =
				validation.error ||
				t(
					"marketing.aiCreditReport.generateForm.validation.invalidDomain",
				);
			setValidationError(errorMessage);
			return;
		}

		// Submit with properly normalized domain and location
		mutation.mutate({ domain: validation.domain!, location });
	};

	const hasError = validationError || mutation.isError;

	// Disable button if mutation is pending OR if there's an active report being generated
	const isButtonDisabled: boolean = mutation.isPending || isReportGenerating;

	// If a report is actively generating, show the generating panel instead of the form
	if (isActivelyGenerating && report) {
		return (
			<ReportGeneratingPanel
				publicToken={report.publicToken}
				domain={report.domain}
				status={report.status}
				progressStep={report.progressStep}
				progressPercent={report.progressPercent}
				progressMessage={report.progressMessage}
			/>
		);
	}

	return (
		<div className="space-y-6">
			<form
				className="flex flex-col gap-4 justify-center max-w-2xl mx-auto"
				onSubmit={handleSubmit}
			>
				<div className="space-y-4">
					<div className="flex flex-col sm:flex-row gap-4">
						<div className="flex-1">
							<label
								htmlFor="ai-visibility-report-domain"
								className="block text-sm font-medium text-gray-700 mb-2"
							>
								{t(
									"marketing.aiCreditReport.generateForm.domainLabel",
									{ defaultValue: "Website Domain" },
								)}
							</label>
							<div className="relative">
								<Input
									id="ai-visibility-report-domain"
									placeholder={t(
										"marketing.aiCreditReport.generateForm.placeholder",
									)}
									value={domain}
									onChange={handleDomainChange}
									data-testid="domain-input"
									disabled={isButtonDisabled}
									className={`h-12 text-lg px-4 transition-colors ${
										hasError
											? "border-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50/50"
											: mutation.isSuccess
												? "border-green-300 focus:border-green-500 focus:ring-green-500 bg-green-50/50"
												: ""
									}`}
								/>
								{hasError && (
									<AlertCircleIcon
										className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500"
										data-testid="alert-circle-icon"
									/>
								)}
								{mutation.isSuccess && !hasError && (
									<CheckCircleIcon
										className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500"
										data-testid="check-circle-icon"
									/>
								)}
							</div>
							{validationError && (
								<p
									className="mt-1 text-sm text-red-600 flex items-center gap-1"
									data-testid="validation-error"
								>
									<AlertCircleIcon className="w-4 h-4" />
									{validationError}
								</p>
							)}
						</div>

						<div className="flex items-end">
							<Button
								type="submit"
								variant="primary"
								disabled={isButtonDisabled}
								size="lg"
								className="h-12 px-8 text-lg font-medium"
								data-testid="generate-button"
							>
								{isReportGenerating
									? t(
											"marketing.aiCreditReport.generatingText",
										)
									: mutation.isPending
										? t(
												"marketing.aiCreditReport.generatingText",
											)
										: t(
												"marketing.aiCreditReport.generate",
											)}
							</Button>
						</div>
					</div>

					{/* A fieldset + legend, not a label: LocationInput wraps a
					    third-party autocomplete that mounts its own input, so
					    there is no id for htmlFor to point at. */}
					<fieldset>
						<legend className="block text-sm font-medium text-gray-500 mb-2">
							{t(
								"marketing.aiCreditReport.generateForm.locationLabel",
								{ defaultValue: "Location (Optional)" },
							)}
						</legend>
						<LocationInput
							value={location}
							onChange={setLocation}
							placeholder={t(
								"marketing.aiCreditReport.generateForm.locationPlaceholder",
								{
									defaultValue: "Select location...",
								},
							)}
							disabled={isButtonDisabled}
							data-testid="location-input"
							className="h-10 text-base"
						/>
						<p className="mt-1 text-xs text-gray-500">
							{t(
								"marketing.aiCreditReport.generateForm.locationHelp",
								{
									defaultValue:
										"Specify a location to get region-specific AI search results",
								},
							)}
						</p>
					</fieldset>
				</div>
			</form>

			{report && report.status === "failed" && (
				<div
					className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
					data-testid="status-failed"
				>
					<div className="flex items-start gap-3">
						<XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
						<div className="flex-1">
							<h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
								Report Generation Failed
							</h3>
							<p className="text-sm text-red-700 dark:text-red-300 mb-3">
								{report.errorMessage ||
									t(
										"marketing.aiCreditReport.generateForm.status.failed",
									)}
							</p>
							{report.errorMessage && (
								<div className="mt-3">
									{report.errorMessage
										.toLowerCase()
										.includes("javascript") ||
									report.errorMessage
										.toLowerCase()
										.includes("single-page") ? (
										<a
											href="/docs/help/is-your-site-visible-to-ai-assistants"
											className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
											target="_blank"
											rel="noopener noreferrer"
										>
											Learn how to fix this
											<svg
												className="w-4 h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M13 7l5 5m0 0l-5 5m5-5H6"
												/>
											</svg>
										</a>
									) : report.errorMessage
											.toLowerCase()
											.includes("cloudflare") ? (
										<a
											href="/docs/help/cloudflare-is-blocking-access-to-your-site"
											className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
											target="_blank"
											rel="noopener noreferrer"
										>
											Learn how to fix this
											<svg
												className="w-4 h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M13 7l5 5m0 0l-5 5m5-5H6"
												/>
											</svg>
										</a>
									) : report.errorMessage
											.toLowerCase()
											.includes("robots.txt") ? (
										<a
											href="/docs/help/is-robots-txt-blocking-ai-on-your-site"
											className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
											target="_blank"
											rel="noopener noreferrer"
										>
											Learn how to fix this
											<svg
												className="w-4 h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M13 7l5 5m0 0l-5 5m5-5H6"
												/>
											</svg>
										</a>
									) : null}
								</div>
							)}
						</div>
					</div>
				</div>
			)}

			{report && report.status === "cancelled" && (
				<StatusBadge
					type="failed"
					message={
						report.errorMessage ||
						t(
							"marketing.aiCreditReport.generateForm.status.cancelled",
						)
					}
					testId="status-cancelled"
				/>
			)}
		</div>
	);
}
